import { FC, useEffect } from 'react';
import cls from 'classnames';
import { useAtomValue } from 'jotai';
import { $UserAgent } from '@lemon8/web-app-shared/atom';
import { EmailVerifyContextReducerKey, ReportProgress, useEmailVerifyReducer } from '~/atoms/email-verify';
import { useSearchParams } from '@remix-run/react';
import Nav from '~/components/nav';
import { ReportForm, ReportFormProps } from '~/components/report-form';
import { ReportType } from 'shared/types/report';
import { FormType } from '~/components/report-form/form-design/type/form';
import { closeWebview } from '~/utils/webview';

export const Copyright: FC = () => {
  const { isMobile, isInApp } = useAtomValue($UserAgent);

  const [state, dispatch] = useEmailVerifyReducer();
  const { progress } = state;
  const [search] = useSearchParams();

  const reportUrl = decodeURIComponent(search.get('report_url') || '');

  const reportEmail = decodeURIComponent(search.get('report_email') || '');

  useEffect(() => {
    if (progress === ReportProgress.RESULT) {
      if (isInApp) {
        // inapp close the webview
        void closeWebview();
      } else {
        history.go(-3);
      }
    } else {
      dispatch({
        type: EmailVerifyContextReducerKey.UPDATE_PROGRESS,
        payload: {
          progress: ReportProgress.REPORT_FORM,
        },
      });
    }
  }, [progress, dispatch]);

  const data: ReportFormProps = {
    reportType: ReportType.COPYRIGHT,
    title: 'Report Copyright Infringement',
    des: 'This form is for infringements in user-generated content, including sponsored content. ',
    reportGroupData: [
      {
        title: '1. Contact Information',
        des: 'Your contact info may be shared with the person who posted the content that you’re reporting. You may prefer to provide your business or professional contact info.',
        formList: [
          {
            label: 'Your full name',
            type: FormType.INPUT,
            name: 'ownerName',
            placeHolder: 'Please fill out your full name',
            required: true,
          },
          {
            label: 'Name of the copyright owner',
            type: FormType.INPUT,
            name: 'legalOwner',
            placeHolder: "Please fill out copyright owner's name",
            required: true,
          },
          {
            label: 'Your physical address',
            type: FormType.INPUT,
            name: 'address',
            placeHolder: 'Please fill out your physical address',
            required: true,
          },
          {
            label: 'Your phone number',
            type: FormType.INPUT,
            name: 'phone',
            placeHolder: 'Please fill out your phone number',
            required: true,
          },
          {
            label: 'Your email address',
            type: FormType.INPUT,
            name: 'email',
            placeHolder: 'Please fill out your email address',
            required: true,
            disabled: Boolean(reportEmail),
            defaultValue: reportEmail,
            rules: [
              {
                regexp: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w+)+$/,
                error: 'Enter a valid email address.',
                trigger: 'blur',
              },
            ],
          },
        ],
      },
      {
        title: '2. Copyright ownership',
        des: "A report alleging infringement or violation of legal rights must come from the trademark owner or someone authorized to report on their behalf. If you aren't the trademark owner or an authorized representative, we won't be able to process your report.",
        formList: [
          {
            label: 'Your relationship to the copyright owner',
            type: FormType.RADIO,
            name: 'relationship',
            options: [
              {
                symbol: '1',
                label: 'I am the copyright owner',
                value: false,
              },
              {
                symbol: '2',
                label: 'I am a host, office, or director (non-legal) of the copyright owner',
                value: false,
              },
              {
                symbol: '3',
                label: 'I am the legal counsel to the copyright owner',
                value: false,
              },
              {
                symbol: '4',
                label: 'I am an employee of the copyright owner',
                value: false,
              },
              {
                symbol: '5',
                label: 'I am an authorized agent of the copyright owner',
                value: false,
              },
            ],
            required: true,
          },
          {
            label: 'Your relationship to the copyright owner',
            tip: [
              'Provide a certificate, license agreement, and/or other materials to prove you are the copyright owner. This is critical to show proof of authority. You can upload a scanned copy of these materials.',
              "If you are authorized to act on behalf of the copyright owner, upload a copy of the power of attorney. If you don't have a power of attorney, upload a signed statement to explain your relationship to the copyright owner. Including all of the necessary documents will help us process your request more efficiently.",
            ],
            type: FormType.FILE,
            name: 'proofFiles',
            buttonText: 'Upload',
            required: true,
          },
        ],
      },
      {
        title: '3. Content to report',
        required: !reportUrl,
        formList: [
          {
            type: FormType.INPUT,
            name: 'contentURL',
            placeHolder: 'e.g. https://v.lemon8-app.com/s/xxx',
            defaultValue: reportUrl,
            required: true,
            disabled: Boolean(reportUrl),
          },
        ],
      },
      {
        title: '4. About your copyrighted work',
        formList: [
          {
            label: 'Type of copyrighted work',
            name: 'contentType',
            type: FormType.RADIO,
            options: [
              {
                symbol: '2',
                label: 'Video',
                value: false,
              },
              {
                symbol: '1',
                label: 'Original Music',
                value: false,
              },
              {
                symbol: '8',
                label: 'Non-Music Audio',
                value: false,
              },
              {
                symbol: '3',
                label: 'Photo / Picture',
                value: false,
              },
              {
                symbol: '5',
                label: 'Logo',
                value: false,
              },
              {
                symbol: '6',
                label: 'Other',
                value: false,
              },
            ],
            required: true,
          },
          {
            label: 'Description of copyrighted work',
            tip: 'Include a clear and complete description of your copyrighted work that you believe was infringed.',
            type: FormType.TEXTAREA,
            name: 'typeDescription',
            placeHolder: 'Description',
            required: true,
          },
        ],
      },
      {
        title: '5. Additional materials (optional)',
        des: 'Include any other materials that can support your report',
        formList: [
          {
            type: FormType.FILE,
            name: 'materialFiles',
            buttonText: 'Upload',
            required: false,
          },
        ],
      },
      {
        title: '6. Statement',
        formList: [
          {
            label: 'Check the applicable statements below. All statements must be true for you to submit your report. ',
            type: FormType.CHECKBOX,
            name: 'statement',
            options: [
              {
                symbol: '1',
                label:
                  'I have a good faith belief that the reported use of the material, in the manner complained of, is not authorized by the copyright owner, its agent, or the law.',
                value: false,
              },
              {
                symbol: '2',
                label:
                  'I state under penalty of perjury that the above information is accurate, and that I am the copyright owner or am authorized to act on behalf of the copyright owner.',
                value: false,
              },
              {
                symbol: '3',
                label:
                  'I acknowledge that all information submitted in my copyright infringement notification may be forwarded to the uploader of the content, to lumendatabase.org, or otherwise made public in any way by Lemon8 or a third party.',
                value: false,
              },
            ],
            required: true,
            rules: [
              {
                error: 'Please fill out the required field.',
                validator: (value: any) => {
                  return new Promise((resolve) => {
                    resolve(value.length === 3 && value.length > 0);
                  });
                },
              },
            ],
          },
        ],
      },
      {
        title: '7. Signature',
        formList: [
          {
            label: 'Sign your name electronically',
            name: 'signature',
            type: FormType.INPUT,
            tip: 'Include the signature of the copyright owner or an authorized representative of the copyright owner. You can enter your full legal name as your electronic signature.',
            placeHolder: 'Your signature',
            required: true,
          },
        ],
      },
    ],
  };

  return (
    <>
      {!isMobile && <Nav isShowSwitcher={false} />}
      <div
        className={cls('report-copyright', {
          pc: !isMobile,
        })}
      >
        <ReportForm {...data} />
      </div>
    </>
  );
};
